import { Strings } from "@/locales/types/enums/strings";
import { Services } from "@ca.vmedia.tv/services";
import type { AdultPin, OktaDevice, OktaToken, VerificationCode, RegisterTrialResponse } from "@ca.vmedia.tv/domains";
import { RegistrationStatus, AppError, AppErrorCode } from "@ca.vmedia.tv/domains";
import { type LoginActionParams, LoginRequestMutationParams, LoginSuccessMutationParams, LoginWebSocketSuccessMutationParams, type ExposeInterface } from './types';
import { Logger } from '@/utils';
import { useMedia } from "@/stores/Media";
import { useLinesHomeStore } from "@/stores/LinesHome";
import { useUserInfoStore } from '../UserInfo';
import { useUserSettingsStore } from '../UserSettings';
import { useParentControlStore } from '../ParentControl';
import { usePlayerStore } from '../Player';
import { useGenresStore } from "@/stores/Genres";
import { useChannelsStore } from "@/stores/Channels";
import { useClientMessagesStore } from "@/stores/ClientMessages";
import { useGuideStore } from "@/stores/Guide";
import { useDetailPageStore } from "@/stores/DetailPage";
import { useVodPageStore } from "@/stores/VodPage";
import { useApplicationStore } from "@/stores/Application";
import { useSearchPageStore } from "@/stores/SearchPage";
import { type AxiosError } from 'axios';
import type {
    ClientAppSettings as ApiClientAppSettings,
} from "@ca.vmedia.tv/swagger-api";

export const useAccountStore = defineStore('Account', (): ExposeInterface => {
    const GuideStoreModule = useGuideStore();
    const {
        resetGuideFilter,
        clearAll: clearAllGuide
    } = GuideStoreModule;

    const ApplicationStore = useApplicationStore();
    const { clearAllApplication, showOktaTokenErrorModal, showServerErrorModal } = ApplicationStore;
    
    const { $i18n, $domains, $sentry, $analytics } = useNuxtApp();

    const { clearAllGenres } = useGenresStore();

    const { getChannels } = useChannelsStore();

    const ClientMessagesStore = useClientMessagesStore();
    const {
        stopReceiving
    } = ClientMessagesStore;


    const {
        setSessionID,
        setLogin,
        setUserEmail,
        setPassword,
        setToInitialState: setUserInfoToInitialState,
    } = useUserInfoStore();

    const {
        setToInitialState: setUserSettingsToInitialState,
    } = useUserSettingsStore();

    const {
        setToInitialState: setParentControlToInitialState,
    } = useParentControlStore();

    const _checkedRating: Ref<AdultPin> = ref({title: "", type: [0]});
    const _checkedTimes: Ref<AdultPin> = ref({title: "", type: 0});
    const _status: Ref<number> = ref(0);
    /**
     * The property is responsible for the visibility of the forgot modal
     */
    const _isForgotModalVisible: Ref<boolean> = ref(false);
    const _oktaDevice: Ref<OktaDevice | null> = ref(null);
    const _oktaToken: Ref<OktaToken | null> = ref(null);
    const _verificationCode: Ref<VerificationCode | null> = ref(null);
    const _helpQrExpiresAt: Ref<string | null> = ref(null);

    const getCheckedRating = computed((): AdultPin => _checkedRating.value);
    const getCheckedTimes = computed((): AdultPin => _checkedTimes.value);
    const getStatus = computed((): number => _status.value); // TODO: deprecated, remove if needn't
    const getForgotModalVisible = computed((): boolean => _isForgotModalVisible.value);
    const getOktaDevice = computed((): OktaDevice | null => _oktaDevice.value);
    const getOktaToken = computed((): OktaToken | null => _oktaToken.value);
    const getErrorMessage = (errorCode: string): string => {
        switch (errorCode) {
            case AppErrorCode.LOGIN_NOT_FOUND:
                return $i18n.t(Strings.LOGIN_ERR_INVALID_CREDENTIALS).toString();
            case AppErrorCode.LOGIN_NETWORK_ERROR:
                return $i18n.t(Strings.LOGIN_ERR_NETWORK).toString();
            case AppErrorCode.LOGIN_LOCATION_DENIED:
                return $i18n.t(Strings.LOGIN_ERR_INVALID_LOCATION).toString();
            case AppErrorCode.SETTINGS_NO_SUBSCRIPTION_ERROR:
                return $i18n.t(Strings.LOGIN_ERR_NO_SUBSCRIPTIONS).toString();
            case AppErrorCode.NOT_SUPPORTED_PLATFORM:
                return $i18n.t(Strings.NOT_SUPPORTED_PLATFORM).toString();
            case AppErrorCode.FATAL_ERROR:
                return $i18n.t(Strings.FATAL_ERROR).toString();
            case AppErrorCode.CHUNK_ERROR:
                return $i18n.t(Strings.CHUNK_ERROR).toString();
            default:
                return $i18n.t(Strings.LOGIN_ERR_OTHER).toString();
        }
    };
    const getVerificationCode = computed((): VerificationCode | null => _verificationCode.value);
    const getHelpQrExpiresDate = computed((): string | null => _helpQrExpiresAt.value);

    /**
     * Login action
     * @param params LoginActionParams Login parameters: domains plugin to perform an api request, username and password
     */
    const Login = async (params: LoginActionParams): Promise<void> => {
        LoginRequest(new LoginRequestMutationParams(params.username, params.password))
        try {
            const user = await $domains.clientRepository.login(
                params.username,
                params.password
            );
            const { errorCode, adultPin, vcloudDelay, errorMessage } = await Services.Instance.client.parseSettings(user);

            if (errorCode !== AppErrorCode.OK) {
                throw new AppError(errorCode as AppErrorCode, errorMessage);
            } else {
                // save data in local storage for auto-login
                await $domains.saveUserInfo(
                    params.username ?? '',
                    params.password ?? '',
                    user.clientCredentials?.sessionId ?? null,
                    user.clientCredentials?.StbMacAddress ?? null,
                    user.clientCredentials?.LoginCountry ?? null,
                    user.clientCredentials?.UserSystemID ?? null,
                    user.clientCredentials?.RefreshToken ?? null,
                    user.clientCredentials?.UserEmail ?? null,
                    adultPin ?? null,
                    vcloudDelay ?? null,
                    user.clientCredentials?.IsOnNet ?? null
                );
                // Call LoginSuccess to setup the state on login success
                LoginSuccess(new LoginSuccessMutationParams(user));
                resetGuideFilter();
            }
        } catch (error) {
            LoginFailure();

            if (error instanceof AppError) {
                handleAppError(error as AppError);
            } else {
                handleError(error as AxiosError);
            }
        }
    };

    /**
     * Logout action
     */
    const Logout = async (): Promise<void> => {
        try {
            const { featureOktaLoginEnabled } = useAppConfig();

            if (featureOktaLoginEnabled) {
                const refreshToken = getOktaToken.value?.refreshToken || JSON.parse(localStorage.getItem("RefreshToken")!).token;
                await $domains.oktaRepository.logoutOkta(refreshToken);
            } else {
                await $domains.clientRepository.logout();
            }

            const {
                clearAll: clearAllMedia,
            } = useMedia();
            const {
                clearAllLinesHome: clearAllOnTv,
            } = useLinesHomeStore();
            const {
                setToInitialState: setPlayerToInitialState,
            } = usePlayerStore();
            const { 
                clearAllDetailPage,
            } = useDetailPageStore();
            const {
                clearAllVodPage,
            } = useVodPageStore();
            const {
                clearAllSearchPage,
            } = useSearchPageStore();
            
            $domains.lsCacher.purge();
            $domains.idbCacher.purge().catch((e) => {
                console.error(e);
            });
            LogoutSuccess();
            // Clear every app store
            clearAllApplication();
            clearAllGenres();
            clearAllGuide();
            clearAllMedia();
            clearAllOnTv();
            clearAllSearchPage();
            clearAllVodPage();
            setPlayerToInitialState();
            clearAllDetailPage();
            stopReceiving();
            await getChannels();
            $domains.lsCacher.del('Rating');
            $domains.lsCacher.del('Times');
            resetGuideFilter();
            $sentry.setUserId(null);
        } catch (error) {
            Logger.captureError(error as AxiosError);
        }
    };

    const handleAppError = (error: AppError): void => {
        if (error && error.code && (error.code === AppErrorCode.OKTA_INVALID_REFRESH_TOKEN || error.code === AppErrorCode.OKTA_INVALID_ACCESS_TOKEN)) {
            showOktaTokenErrorModal(true);
        } else {
            throw error;
        }
    };
    const handleError = (error: AxiosError): void => {
        if (error.response && error.response.status && error.response.status >= 500 && error.response.status < 600) {
            showServerErrorModal(true);
        } else {
            throw new AppError(AppErrorCode.LOGIN_NETWORK_ERROR);
        }
        Logger.captureError(error as AxiosError);
    };

    const FetchOktaDevice = async (): Promise<void> => {
        try {
            const templates: OktaDevice = await $domains.oktaRepository.getAuthenticationLink();
            _oktaDevice.value = templates
        } catch (error) {
            handleError(error as AxiosError);
        }
    };

    const FetchOktaToken = async (deviceCode: string): Promise<void> => {
        try {
            const templates: OktaToken = await $domains.oktaRepository.getToken(deviceCode);
            localStorage.setItem("RefreshToken", JSON.stringify({"token": templates.refreshToken}));
            _oktaToken.value = templates;
        } catch (error) {
            handleError(error as AxiosError);
        }
    };

    const OktaLogin = async (accessToken: string): Promise<void> => {
        try {
            const user = await $domains.oktaRepository.loginOkta(accessToken);
            const { errorCode, adultPin, vcloudDelay, errorMessage } = await Services.Instance.client.parseSettings(user);

            if (errorCode !== AppErrorCode.OK) {
                // Commit LOGIN_FAILURE mutation to setup the state on login failure
                throw new AppError(errorCode as AppErrorCode, errorMessage);
            } else {
                // save data in local storage for auto-login
                await $domains.saveUserInfo(
                    user.clientCredentials?.UserLogin ?? '',
                    user.clientCredentials?.UserPassword ?? '',
                    user.clientCredentials?.sessionId ?? null,
                    user.clientCredentials?.StbMacAddress ?? null,
                    user.clientCredentials?.LoginCountry ?? null,
                    user.clientCredentials?.UserSystemID ?? null,
                    user?.refreshToken ?? null,
                    user.clientCredentials?.UserEmail ?? null,
                    adultPin ?? null,
                    vcloudDelay ?? null,
                    user.clientCredentials?.IsOnNet ?? null
                );
                LoginRequest(new LoginRequestMutationParams(user.clientCredentials?.UserLogin ?? '', user.clientCredentials?.UserPassword ?? ''));
                LoginSuccess(new LoginSuccessMutationParams(user));
                resetGuideFilter();
            }
        } catch (error) {
            LoginFailure();

           if (error instanceof AppError) {
                handleAppError(error as AppError);
            } else {
                handleError(error as AxiosError);
            }
        }
    };

    const SilentLoginToken = async (): Promise<AppErrorCode | null> => {
        try {
            const { username, UserSystemID, RefreshToken } = await $domains.loadUserInfo();
            if (!RefreshToken || !username || !UserSystemID) {
                throw new AppError(AppErrorCode.LOGIN_TOKEN_ERROR);
            }

            const user = await $domains.clientRepository.loginWithToken(username, UserSystemID, RefreshToken);
            const { errorCode, adultPin, vcloudDelay } = await Services.Instance.client.parseSettings(user);

            if (errorCode !== AppErrorCode.OK) {
                throw new AppError(AppErrorCode.LOGIN_TOKEN_ERROR);
            } else {
                // save data in local storage for auto-login
                await $domains.saveUserInfo(
                    user.clientCredentials?.UserLogin ?? '',
                    user.clientCredentials?.UserPassword ?? '',
                    user.clientCredentials?.sessionId ?? null,
                    user.clientCredentials?.StbMacAddress ?? null,
                    user.clientCredentials?.LoginCountry ?? null,
                    user.clientCredentials?.UserSystemID ?? null,
                    RefreshToken,
                    user.clientCredentials?.UserEmail ?? null,
                    adultPin ?? null,
                    vcloudDelay ?? null,
                    user.clientCredentials?.IsOnNet ?? null
                );
                LoginRequest(new LoginRequestMutationParams(user.clientCredentials?.UserLogin ?? '', user.clientCredentials?.UserPassword ?? ''));
                LoginSuccess(new LoginSuccessMutationParams(user));
                resetGuideFilter();
            }
            return AppErrorCode.OK;
        } catch (error) {
            LoginFailure();

            if (error instanceof AppError) {
                handleAppError(error as AppError);
            } else {
                handleError(error as AxiosError);
            }
            return null;
        }
    };

    const SilentLoginOkta = async (): Promise<AppErrorCode | null> => {
        try {
            const refreshToken = JSON.parse(localStorage.getItem("RefreshToken")!).token;
            if (!refreshToken) {
                throw new AppError(AppErrorCode.LOGIN_TOKEN_ERROR);
            }
            const user = await $domains.oktaRepository.silentLoginOkta(refreshToken);
            localStorage.setItem("RefreshToken", JSON.stringify({"token": user.refreshToken}));
            const { errorCode, adultPin, vcloudDelay, errorMessage } = await Services.Instance.client.parseSettings(user);

            if (errorCode !== AppErrorCode.OK) {
                throw new AppError(errorCode as AppErrorCode, errorMessage);
            } else {
                // save data in local storage for auto-login
                await $domains.saveUserInfo(
                    user.clientCredentials?.UserLogin ?? '',
                    user.clientCredentials?.UserPassword ?? '',
                    user.clientCredentials?.sessionId ?? null,
                    user.clientCredentials?.StbMacAddress ?? null,
                    user.clientCredentials?.LoginCountry ?? null,
                    user.clientCredentials?.UserSystemID ?? null,
                    user?.refreshToken ?? null,
                    user.clientCredentials?.UserEmail ?? null,
                    adultPin ?? null,
                    vcloudDelay ?? null,
                    user.clientCredentials?.IsOnNet ?? null
                );
                LoginRequest(new LoginRequestMutationParams(user.clientCredentials?.UserLogin ?? '', user.clientCredentials?.UserPassword ?? ''));
                LoginSuccess(new LoginSuccessMutationParams(user));
                resetGuideFilter();
            }
            return AppErrorCode.OK;
        } catch (error) {
            LoginFailure();

            if (error instanceof AppError) {
                handleAppError(error as AppError);
            }else {
                handleError(error as AxiosError);
            }
            return null;
        }
    };

    const LoginWithMacAddress = async (): Promise<AppErrorCode | null> => {
        try {
            let user = await $domains.clientRepository.loginWithMacAddress();
        
            const { macAddress, deviceId } = $domains.getDeviceInformation();
            const { errorCode, adultPin, vcloudDelay, errorMessage } = await Services.Instance.client.parseSettings(user);

            const isLocationOrNetworkError = [AppErrorCode.LOGIN_LOCATION_DENIED, AppErrorCode.LOGIN_NETWORK_ERROR].includes(errorCode as AppErrorCode);

            const isUserOrFoundError = [AppErrorCode.LOGIN_NOT_FOUND, AppErrorCode.LOGIN_NO_USER].includes(errorCode as AppErrorCode);

            if (isLocationOrNetworkError) {
                throw new AppError(errorCode as AppErrorCode, errorMessage);
            }
        
            if (isUserOrFoundError && macAddress && deviceId) {
                const { registrationStatus, session }: RegisterTrialResponse = await $domains.clientRepository.registerWithMacAddress(
                    macAddress,
                    deviceId
                );
        
                if (registrationStatus === RegistrationStatus.Success && session) {
                    user = session;
                }
            }
        
            if (user !== null) {
                await $domains.saveUserInfo(
                    user.clientCredentials?.UserLogin ?? '',
                    user.clientCredentials?.UserPassword ?? '',
                    user.clientCredentials?.sessionId ?? null,
                    user.clientCredentials?.StbMacAddress ?? null,
                    user.clientCredentials?.LoginCountry ?? null,
                    user.clientCredentials?.UserSystemID ?? null,
                    user.clientCredentials?.RefreshToken ?? null,
                    user.clientCredentials?.UserEmail ?? null,
                    adultPin ?? null,
                    vcloudDelay ?? null,
                    user.clientCredentials?.IsOnNet ?? null
                );
                LoginRequest(new LoginRequestMutationParams(user.clientCredentials?.UserLogin ?? '', user.clientCredentials?.UserPassword ?? ''));
                LoginSuccess(new LoginSuccessMutationParams(user));
                resetGuideFilter();
            }
            return AppErrorCode.OK;
        } catch (error) {
            LoginFailure();

            if (error instanceof AppError) {
                handleAppError(error as AppError);
            }else {
                handleError(error as AxiosError);
            }
            return null;
        }
    };

    const FetchVerificationCode = async (): Promise<void> => {
        try {
            const templates: VerificationCode = await $domains.clientRepository.getVerificationCode();
            _verificationCode.value = templates;
        } catch (error) {
            Logger.captureError(error as AxiosError);
        }
    };

    const SaveUserInfo = async (templates: string): Promise<void> => {
        try {
            const _user: ApiClientAppSettings = JSON.parse(templates);
            const user = await $domains.clientRepository.convertApiAppSettings(_user);
            LoginRequest(new LoginRequestMutationParams(user.clientCredentials?.UserLogin ?? '', user.clientCredentials?.UserPassword ?? ''));
            const { errorCode, adultPin, vcloudDelay, errorMessage } = await Services.Instance.client.parseSettings(user);
            if (errorCode !== AppErrorCode.OK) {
                throw new AppError(errorCode as AppErrorCode, errorMessage);
            } else {
                // save data in local storage for auto-login
                await $domains.saveUserInfo(
                    user.clientCredentials?.UserLogin ?? '',
                    user.clientCredentials?.UserPassword ?? '',
                    user.clientCredentials?.sessionId ?? null,
                    user.clientCredentials?.StbMacAddress ?? null,
                    user.clientCredentials?.LoginCountry ?? null,
                    user.clientCredentials?.UserSystemID ?? null,
                    user.clientCredentials?.RefreshToken ?? null,
                    user.clientCredentials?.UserEmail ?? null,
                    adultPin ?? null,
                    vcloudDelay ?? null,
                    user.clientCredentials?.IsOnNet ?? null
                );
                // Call LoginSuccess to setup the state on login success
                LoginSuccess(new LoginWebSocketSuccessMutationParams(user));
            }
        } catch (error) {
            LoginFailure();
            Logger.captureError(error as AxiosError);
        }
    };

    const GetHelpQrExpiresDate = async ({ value }: { value: string }): Promise<void> => {
        try {
            const { result } = await $domains.clientRepository.setSettingsExternal(value);
            _helpQrExpiresAt.value = result;
        } catch (error) {
            Logger.captureError(error as AxiosError);
        }
    };



    /**
     * The mutation called on the login request.
     * @param params The login and password parameters to setup on login request.
     */
    const LoginRequest = (params: LoginRequestMutationParams): void => {
        setLogin(params.username);
        setPassword(params.password ?? null);
        setSessionID(null);
    };
    /**
     * The mutation called on the login success.
     * @param params The sessionID parameter to setup for the future use in the application api calls.
     */
    const LoginSuccess = (params: LoginSuccessMutationParams | LoginWebSocketSuccessMutationParams): void => {
        setUserEmail(params.user?.clientCredentials?.UserEmail ?? null);
        setSessionID(params.sessionId);
        $sentry.setUserId(params.UserSystemID);
        $analytics.createEventLogin(params.UserSystemID);
    };

    const SaveLogin = (params: LoginRequestMutationParams): void => {
        setLogin(params.username);
    };

    /**
     * The mutation called on the login failed.
     */
    const LoginFailure = (): void => {
        setLogin(null);
        setUserEmail(null);
        setPassword(null);
        setSessionID(null);
    };
    /**
     * The mutation called on the logout.
     */
    const LogoutSuccess = (): void => {
        setToInitialState();
        setUserInfoToInitialState();
        setUserSettingsToInitialState();
        setParentControlToInitialState();
    };

    const ClearLogin = (): void => {
        setLogin(null);
        setUserEmail(null);
        setPassword(null);
        setSessionID(null);
        setUserSettingsToInitialState();
    };

    // TODO: deprecated, remove if needn't
    const SetStatus = (): void => {
        _status.value++;
    };

    const ShowForgotModal = (): void => {
        _isForgotModalVisible.value = true;
    };

    const HideForgotModal = (): void => {
        _isForgotModalVisible.value = false;
    };

    const setToInitialState = (): void => {
        _checkedRating.value = {title: "", type: [0]};
        _checkedTimes.value = {title: "", type: 0};
        _status.value = 0;
        _isForgotModalVisible.value = false;
        _oktaDevice.value = null;
        _oktaToken.value = null;
        _verificationCode.value = null;
        _helpQrExpiresAt.value = null;
    };

    return {
        /** Getters */
        getCheckedRating,
        getCheckedTimes,
        getStatus,
        getForgotModalVisible,
        getOktaDevice,
        getOktaToken,
        getVerificationCode,
        getHelpQrExpiresDate,
        getErrorMessage,
        /** Actions */
        Login,
        Logout,
        FetchOktaDevice,
        FetchOktaToken,
        OktaLogin,
        SilentLoginToken,
        SilentLoginOkta,
        LoginWithMacAddress,
        FetchVerificationCode,
        SaveUserInfo,
        GetHelpQrExpiresDate,
        /** Mutations */
        LoginSuccess,
        SaveLogin,
        ClearLogin,
        SetStatus,
        ShowForgotModal,
        HideForgotModal,
    };
});